import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { FaCloudArrowUp, FaSpinner } from "react-icons/fa6";
import { uploadMenuCardImage } from "../../api/menuCards.js";
import { addToast } from "../../store/app/action.js";
import { REQUIRED_WIDTH, REQUIRED_HEIGHT } from "./constants.js";

/**
 * Trailing "add image" tile that sits inline at the end of a region's card
 * strip. Validates the image is exactly REQUIRED_WIDTH x REQUIRED_HEIGHT before
 * uploading to S3, then hands the resulting card up to the parent.
 *
 * @param {(card: {image: string, link: string}) => void} props.onCardAdded
 */
const CardDropzone = ({ onCardAdded }) => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const toast = (type, message, timer = 6) =>
    dispatch(addToast({ type, message, timer }));

  // Validate exact dimensions client-side before uploading. Resolves with the
  // file when valid, rejects (after toasting) otherwise.
  const validateDimensions = (file) =>
    new Promise((resolve, reject) => {
      if (!file.type.startsWith("image/")) {
        toast("error", "Please choose a valid image file.");
        return reject();
      }
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        if (img.width !== REQUIRED_WIDTH || img.height !== REQUIRED_HEIGHT) {
          toast(
            "error",
            `Image must be exactly ${REQUIRED_WIDTH}x${REQUIRED_HEIGHT}px — this one is ${img.width}x${img.height}px.`
          );
          return reject();
        }
        resolve(file);
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        toast("error", "Could not read that image file.");
        reject();
      };
      img.src = objectUrl;
    });

  const handleFile = async (file) => {
    if (!file || uploading) return;
    let valid;
    try {
      valid = await validateDimensions(file);
    } catch {
      return; // toast already shown
    }
    setUploading(true);
    try {
      const url = await uploadMenuCardImage(valid);
      if (!url) throw new Error("No URL returned");
      onCardAdded({ image: url, link: "" });
    } catch (err) {
      toast("error", "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const onInputChange = (e) => {
    const file = e.target.files?.[0];
    handleFile(file);
    // reset so re-selecting the same file fires onChange again
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  };

  return (
    <button
      type="button"
      onClick={() => !uploading && inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setDragActive(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setDragActive(false);
      }}
      onDrop={onDrop}
      disabled={uploading}
      className={
        "shrink-0 w-60 aspect-[3/2] rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-2 text-sm transition-colors " +
        (dragActive
          ? "border-theme-button-primary bg-sky-50 dark:bg-sky-500/10 text-theme-button-primary"
          : "border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-theme-button-primary hover:text-theme-button-primary")
      }
    >
      {uploading ? (
        <>
          <FaSpinner className="w-5 h-5 animate-spin" />
          <span>Uploading…</span>
        </>
      ) : (
        <>
          <FaCloudArrowUp className="w-6 h-6" />
          <span className="font-medium">Add image</span>
          <span className="text-xs">
            Drag &amp; drop or click · {REQUIRED_WIDTH}×{REQUIRED_HEIGHT}
          </span>
        </>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onInputChange}
      />
    </button>
  );
};

export default CardDropzone;
